import * as React from "react";

import "./image-object.scss";

import {cs}    from "@Common/react/chain-services";
import {chain} from "@Common/utils/fs";

import {alterPointByPixel} from "../common/alter-point-by-pixel";
import {centerOfSize}      from "../common/center-of-size";
import {floorVector1}      from "../common/floor-size";
import {getImgPoint2}      from "../common/get-img-point-2";
import {LoadRawImg}        from "../common/load-raw-img";
import {roundVector}       from "../common/round-vector";
import {switchSize}        from "../common/switch-size";
import {applyOpacity}      from "../draw-img/apply-opacity";
import {grayscale}         from "../draw-img/grayscale";
import {negative}          from "../draw-img/negative";
import {tint}              from "../draw-img/tint";

export const ImageObject = ({url, adj, next}) => cs(
    ["raw", ({}, next) => LoadRawImg({
        url,
        next,
    })],

    ["cal", ({raw}, next) => next(!raw || raw.loading ? null : {
        center: adj?.center ?? centerOfSize(raw.size),
        flip: adj?.flip,
        rotation: adj?.rotation,
        scale: adj?.scale ?? 1,
    })],
    ({raw, cal}) => next(cal && {
        renderKey: {...cal, opacity: adj?.opacity, filter: adj?.filter}, // For Preview
        rawSize: !cal ? null : (![90, 270].includes(cal?.rotation) ? raw.size : switchSize(raw.size)),
        getCenter: (p, {vpSize}) => getImgPoint2(vpSize, cal, raw.size)(p),

        getColorC: ({vpSize, filter, scale, center, opacity}) => {
            if (!cal) {
                return null;
            }
            const scale1 = scale??cal.scale;
            const getImgPixelPoint = chain(
                getImgPoint2(vpSize, {...cal, scale: scale1, center: center??cal.center}, raw.size),
                (alter) => (p) => alterPointByPixel(p, scale1, alter),
            );

            // console.log(adj?.maskCutout)
            return (p) => {
                const p1 = chain(
                    p,
                    getImgPixelPoint,
                    floorVector1, // TODO anti-alias
                );

                if (!isInBound(p1, raw.size)) {
                    return v=>v;
                }

                const color = chain(
                    p1,
                    // (_) => inBound(_, raw.size),
                    raw.getPColor,
                );
                if (adj?.maskCutout && (!color || color.a === 0 || colorDiff(color, adj.maskCutout.color) < 400)) {
                    return () => colorTransparent;
                }
                const filter1 = filter ?? adj?.filter;
                if (filter1) {
                    if (filter1.type === "grayscale") {
                        grayscale(color);
                    } else if (filter1.type === "negative") {
                        negative(color);
                    } else if (filter1.type === "tint") {
                        tint(color, filter1.color, filter1.strength);
                    }
                }
                return (c1) => applyOpacity(color, c1, opacity ?? adj?.opacity ?? 1);
            };
        },
    }),
);

const colorDiff = (c1, c2) => (
    Math.abs(c1.r - c2.r) +
    Math.abs(c1.g - c2.g) +
    Math.abs(c1.b - c2.b) +
    Math.abs(c1.a - c2.a)
);
const colorTransparent = {r:0,g:0,b:0,a:0,};

const isInBound = (p, vpSize) => chain(
    p,
    roundVector,
    (_) => (
        _.x >= 0 && _.x < vpSize.width &&
        _.y >= 0 && _.y < vpSize.height
    ),
);
